import { useEffect } from 'react'

type SEOHeadProps = {
  title?: string
  description?: string
  canonical?: string
  toolName?: string
}

const BASE_DESCRIPTION =
  'PdfTide: convert PDF to PNG, EPUB to PDF, MD to PDF, IPYNB to PDF, and 50+ more tools — 100% browser-side processing, zero uploads, instant results.'

export default function SEOHead({ title, description, canonical, toolName }: SEOHeadProps) {
  const pageTitle = toolName
    ? `Convert ${toolName} Online - Free & Fast | PdfTide`
    : title || 'PdfTide — Browser-First PDF Utility Hub | 50+ Free Tools'

  const pageDesc = description || BASE_DESCRIPTION
  const pageUrl = canonical || 'https://www.pdftide.com'

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: toolName ? `PdfTide ${toolName} Converter` : 'PdfTide',
    url: pageUrl,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any',
    offers: {
      '@type': 'Offer',
      price: '0.00',
      priceCurrency: 'USD',
    },
    browserRequirements: 'Requires a modern browser with WebAssembly support.',
    description: pageDesc,
  }

  useEffect(() => {
    // Title
    document.title = pageTitle

    // Meta description
    let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]')
    if (!metaDesc) {
      metaDesc = document.createElement('meta')
      metaDesc.name = 'description'
      document.head.appendChild(metaDesc)
    }
    metaDesc.content = pageDesc

    // Canonical
    let linkCanonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!linkCanonical) {
      linkCanonical = document.createElement('link')
      linkCanonical.rel = 'canonical'
      document.head.appendChild(linkCanonical)
    }
    linkCanonical.href = pageUrl

    // OG tags
    const setMeta = (property: string, content: string, isName = false) => {
      const attr = isName ? 'name' : 'property'
      let tag = document.querySelector<HTMLMetaElement>(`meta[${attr}="${property}"]`)
      if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute(attr, property)
        document.head.appendChild(tag)
      }
      tag.content = content
    }

    setMeta('og:title', pageTitle)
    setMeta('og:description', pageDesc)
    setMeta('og:type', 'website')
    setMeta('og:url', pageUrl)
    setMeta('og:site_name', 'PdfTide')
    setMeta('twitter:card', 'summary_large_image', true)
    setMeta('twitter:title', pageTitle, true)
    setMeta('twitter:description', pageDesc, true)
    setMeta('robots', 'index, follow', true)

    // JSON-LD Schema
    const existingScript = document.querySelector('script[data-schema="pdftide"]')
    if (existingScript) existingScript.remove()
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-schema', 'pdftide')
    script.textContent = JSON.stringify(schema)
    document.head.appendChild(script)

    return () => {
      document.querySelector('script[data-schema="pdftide"]')?.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle, pageDesc, pageUrl])

  return null
}
